import { useCallback, useEffect, useRef } from "react";
import type { ProvetLoadStatus } from "../context/provetDataContext";

/**
 * "Try again" for a list that failed to load. The "Try again" link disappears while the
 * list reloads, so focus would fall back to the page. Once the reload has finished (loaded
 * or failed again), focus moves to `targetRef`: a stable container around the list, its
 * messages and its error, which should have `tabIndex={-1}`.
 */
export function useFocusAfterReload(status: ProvetLoadStatus, reload: () => void) {
  const targetRef = useRef<HTMLDivElement>(null);
  const focusPending = useRef(false);

  useEffect(() => {
    if (focusPending.current && status !== "loading") {
      focusPending.current = false;
      targetRef.current?.focus();
    }
  }, [status]);

  const retry = useCallback(() => {
    focusPending.current = true;
    reload();
  }, [reload]);

  return { targetRef, retry };
}
